// @flow
import { get, filter } from 'lodash';
import { ProductBrowserSelector } from 'modules/productBrowser/selector';
import { ProjectBrowserSelector } from 'modules/projectBrowser/selector';
import type { AppState } from 'modules/rootReducer';
import { RouteNames } from 'router';
import type { Contact } from 'types/common';
import { StringUtil } from 'utils/StringUtil';
import { CampaignSelector } from 'modules/campaign/selector';
import { ENVIRONMENT_TYPE } from 'config/env'

const HOMES_VERTICAL_ID = 3;
const value = (ENVIRONMENT_TYPE === 'production') ? 12 : 17;

const getBannerData = state => {
  return state.home.banner;
};
const getHeaderData = state => {
  return state.home.header;
};
const getBannerLoading = state => {
  return state.home.loading.banner;
};

const getBannerError = state => {
  return state.home.error.banner;
};

const getContacts = state => {
  const contactData = state.home.contacts;
  if (contactData) {
    const contactDetails = contactData.contactDetails;
    const vertical_id = contactData.vertical_id;
    if (vertical_id === null) return contactDetails[0];

    return formatContactData(contactData);
  }

  return contactData;
};

const getContactForVertical = (
  state: AppState,
  verticalKey: string
): Contact | null => {
  const contacts = state.home.contacts;
  if (!contacts || contacts.length === 0) {
    return null;
  }

  const verticalId = getVerticalIdForKey(verticalKey);
  if (verticalId) {
    return contacts.find((item: Contact) => item.verticalId === verticalId);
  }
  if (!verticalId) {
    return contacts.find((item: Contact) => item.type === 'TE');
  }
};

const getContactForProject = (state: AppState): Contact | null => {
  const contacts = state.home.contacts;
  if (!contacts || contacts.length === 0) {
    return null;
  }

  const projectDetails = ProjectBrowserSelector.getProjectDetails(state);
  if (!projectDetails) {
    return getContactForHomes(state);
  }

  const contact = contacts.find(
    (item: Contact) =>
      item.verticalId === HOMES_VERTICAL_ID &&
      item.projectId === projectDetails.project.id
  );

  return contact || getContactForHomes(state);
};

const getContactForProduct = (state: AppState): Contact | null => {
  const contacts = state.home.contacts;
  if (!contacts || contacts.length === 0) {
    return null;
  }

  const productDetails = ProductBrowserSelector.getProductDetailsData(state);
  if (!productDetails) {
    return getContactForHomes(state);
  }

  const productId = get(productDetails, 'productDetails[0].id', '');
  const contact = contacts.find(
    (item: Contact) =>
      item.verticalId === HOMES_VERTICAL_ID && item.productId === productId
  );

  return contact || getContactForHomes(state);
};

const getContactForHomes = (state: AppState): Contact | null => {
  const contacts = state.home.contacts;
  if (!contacts || contacts.length === 0) {
    return null;
  }

  return contacts.find((item: Contact) => {
    return (
      item.verticalId === HOMES_VERTICAL_ID &&
      item.projectId == null &&
      item.productId == null
    );
  });
};

const getContactForMenuItems = (
  state: AppState,
  typeName: string
): Contact | null => {
  const contacts = state.home.contacts;
  if (!contacts || contacts.length === 0) {
    return null;
  }
  return contacts.find((item: Contact) => item.type === typeName);
};

const getContactForCampaignDetail = (state: AppState): Contact | null => {
  const contacts = state.home.contacts;
  if (!contacts || contacts.length === 0) {
    return null;
  }

  const campaignDetails = CampaignSelector.getCampaignDetails(state);
  const campaignId = get(campaignDetails[0], 'id', '');
  const contact = contacts.find(
    (item: Contact) => item.campaignId === campaignId
  );

  return contact || [];
};

// TODO:
const getVerticalIdForKey = (key: string): number | null => {
  key = `/${key}`;
  switch (key) {
    case RouteNames.architectureDesign:
      return 1;
    case '/Architecture-and-design':
      return 1;
    case RouteNames.store:
      return 2;
    case '/Store':
      return 2;
    case RouteNames.homes:
      return 3;
    case '/Homes':
      return 3;
    case RouteNames.resorts:
      return 4;
    case '/Resorts':
      return 4;
    case RouteNames.rentals:
      return 6;
    case '/Rentals':
      return 6;
    case RouteNames.workcations:
      return 7;
    case '/Workcations':
      return 7;
    case RouteNames.restaurants:
      return 8;
    case '/Restaurants':
      return 8;
    case RouteNames.musicEvents:
      return 9;
    case '/Music-and-events':
      return 9;
    case RouteNames.campaign:
      return 11;
    case '/Campaign/:campaignSlug':
      return 11;
    case RouteNames.landCraft:
      return value;
    default:
      return null;
  }
};

const getContactsLoading = state => {
  return state.home.loading.contacts;
};

const getContactsError = state => {
  return state.home.error.contacts;
};

const getVerticals = state => {
  return state.home.verticals;
};

const getVerticalsLoading = state => {
  return state.home.loading.verticals;
};

const getVerticalsError = state => {
  return state.home.error.verticals;
};

const getSiteSettings = state => {
  return state.home.siteSettings;
};

const getSiteSettingsLoading = state => {
  return state.home.loading.siteSettings;
};

const getSiteSettingsError = state => {
  return state.home.error.siteSettings;
};

const getBanners = (state, isVertical, vertical, type) => {
  return formatBanners(state.home.banner, isVertical, vertical, type);
};

const isLoading = state => {
  return (
    getBannerLoading(state) &&
    getContactsLoading(state) &&
    getVerticalsLoading(state) &&
    getSiteSettingsLoading(state)
  );
};

const formatContactData = data => {
  let contact = {};
  data.contactDetails.forEach((details, idx) => {
    if (details.vertical_id === data.vertical_id)
      contact = {
        email: details.email,
        phone: details.phone,
        address: details.address,
        title: details.title,
        description: details.description,
      };
  });
  return contact;
};

const extractMediaPath = response => {
  const DEFAULT_DEVICE_MAP = {
    web: 'Web',
    mobile: 'Mobile',
    thumbnail: 'Thumbnail',
  };

  const mediaPath = response && response.media_path;
  const deviceMap = (response && response.device) || DEFAULT_DEVICE_MAP;
  return {
    mobile: `${mediaPath}${deviceMap.mobile}`,
    thumbnail: `${mediaPath}${deviceMap.thumbnail}`,
    web: `${mediaPath}${deviceMap.web}`,
  };
};

const formatBanners = (response, isVertical, vertical, type) => {
  let banner = '';
  const mediaPath = extractMediaPath(response && response.data);
  const bannerData = response && response.data.page_banners;
  if (isVertical) {
    banner = bannerData && bannerForVertical(bannerData, vertical, mediaPath);
  } else if (!isVertical) {
    banner = bannerData && bannerForLinks(bannerData, type, mediaPath);
  }
  return banner;
};

const bannerForVertical = (bannerData, vertical, mediaPath) => {
  let pageBanner = '';
  bannerData.forEach(data => {
    const verticalName = StringUtil.sliceHtml(
      data.vertical && data.vertical.vertical_name
    );
    let name = StringUtil.lowerCaseString(verticalName);
    const extractedName = name.includes(vertical);
    if (extractedName) {
      pageBanner = bannerFields(data, mediaPath);
    }
  });

  return pageBanner;
};

const bannerForLinks = (bannerData, type, mediaPath) => {
  let pageBanner = '';
  bannerData.forEach(data => {
    if (data.type === type) {
      pageBanner = bannerFields(data, mediaPath);
    }
  });

  return pageBanner;
};

const bannerFields = (data, mediaPath) => {
  return {
    title: data.header_title,
    description: data.banner_description,
    description_mobile: data.banner_mobile_description,
    image: `${mediaPath.web}/${data.header_banner}`,
    image_mobile: `${mediaPath.mobile}/${data.mobile_header_banner}`,
    thumbnail: `${mediaPath.thumbnail}/${data.header_banner}`,
    thumbnail_mobile: `${mediaPath.thumbnail}/${data.mobile_header_banner}`,
    metaTitle: data.meta_title ? data.meta_title : '',
    metaDescription: data.meta_description ? data.meta_description : '',
    metaKeywords: data.meta_keyword ? data.meta_keyword : '',
    sub_heading: data.sub_heading,
    banner_video: data.header_video ? data.header_video : null,
  };
};

//TODO: refactor check for conditions (temporary solution)
const showOfferingPages = state => {
  const bannerData = state.home.banner || [];
  const verticalData = state.home.verticals || [];
  const contacts = state.home.contacts || [];
  const banners = bannerData && bannerData.data;
  const pageBanner = banners && banners.page_banners;
  let loyaltyData = [];
  let thinkDesignData = [];
  let craftBeerData = [];
  let propertyData = [];
  let musicData = [];
  let workspace = [];
  let homes = [];
  let restaurants = [];
  let aboutData = [];
  let careerData = [];
  let contactData = [];
  let landCraft = [];
  pageBanner &&
    pageBanner.map(banner => {
      if (banner.type === 'Craft beer') {
        craftBeerData.push(banner);
      }
      if (banner.type === 'Property management') {
        propertyData.push(banner);
      }
      if (banner.type === 'Think design') {
        thinkDesignData.push(banner);
      }
      if (banner.type === 'Loyalty Program') {
        loyaltyData.push(banner);
      }
      if (banner.type === 'About') {
        aboutData.push(banner);
      }
      if (banner.type === 'Career') {
        careerData.push(banner);
      }
    });

  verticalData.map(vertical => {
    if (vertical.id === 9) {
      musicData.push(vertical);
    }
    if (vertical.id === 7) {
      workspace.push(vertical);
    }
    if (vertical.id === 3) {
      homes.push(vertical);
    }
    if (vertical.id === 8) {
      restaurants.push(vertical);
    }
    if (vertical.id === 12) {
      landCraft.push(vertical);
    }
  });

  contacts &&
    contacts.map(contact => {
      if (contact.type === 'TE') {
        contactData.push(contact);
      }
    });

  return {
    loyaltyData,
    thinkDesignData,
    propertyData,
    craftBeerData,
    musicData,
    workspace,
    restaurants,
    homes,
    aboutData,
    careerData,
    contactData,
    landCraft,
  };
};

export const HomeSelector = {
  getBannerData,
  getHeaderData,
  getBannerLoading,
  getBannerError,
  getContacts,
  getContactsLoading,
  getContactsError,
  getContactForVertical,
  getContactForProject,
  getContactForProduct,
  getContactForMenuItems,
  getVerticals,
  getVerticalsLoading,
  getVerticalsError,
  getVerticalIdForKey,
  getSiteSettings,
  getSiteSettingsLoading,
  getSiteSettingsError,
  isLoading,
  getBanners,
  showOfferingPages,
  getContactForCampaignDetail,
};
